import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { CreateShipmentRequest, UpdateShipmentRequest } from '@domain/purchasing/IShipmentRepository';

const SHIPMENTS_QUERY_KEY = 'shipments';
const SHIPMENT_QUERY_KEY = 'shipment';

export const useShipments = () => {
  const queryClient = useQueryClient();

  const { data: shipments, isLoading, error } = useQuery({
    queryKey: [SHIPMENTS_QUERY_KEY],
    queryFn: async () => {
      const result = await container.shipmentRepository.getShipments();
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    }
  });

  const createShipmentMutation = useMutation({
    mutationFn: async (request: CreateShipmentRequest) => {
      const result = await container.shipmentRepository.createShipment(request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate the shipments list
      queryClient.invalidateQueries({ queryKey: [SHIPMENTS_QUERY_KEY] });
      // Also invalidate any purchase order queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  const updateShipmentMutation = useMutation({
    mutationFn: async ({ id, request }: { id: string; request: Partial<UpdateShipmentRequest>; purchaseOrderId?: string }) => {
      const result = await container.shipmentRepository.updateShipment(id, request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id, purchaseOrderId }) => {
      // Invalidate the shipments list
      queryClient.invalidateQueries({ queryKey: [SHIPMENTS_QUERY_KEY] });
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_QUERY_KEY, id] });
      // Also invalidate any purchase order queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', purchaseOrderId] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  const addPurchaseOrderItemsToShipmentMutation = useMutation({
    mutationFn: async ({ id, request }: { id: string; request: { purchaseOrderItemIds: string[] } }) => {
      const result = await container.shipmentRepository.addPurchaseOrderItemsToShipment(id, request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate the shipments list
      queryClient.invalidateQueries({ queryKey: [SHIPMENTS_QUERY_KEY] });
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_QUERY_KEY, id] });
      // Also invalidate any purchase order queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  const removePurchaseOrderItemsFromShipmentMutation = useMutation({
    mutationFn: async ({ id, request }: { id: string; request: { purchaseOrderItemIds: string[] } }) => {
      const result = await container.shipmentRepository.removePurchaseOrderItemsFromShipment(id, request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate the shipments list
      queryClient.invalidateQueries({ queryKey: [SHIPMENTS_QUERY_KEY] });
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_QUERY_KEY, id] });
      // Also invalidate any purchase order queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  const deleteShipmentMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.shipmentRepository.deleteShipment(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, id) => {
      // Invalidate the shipments list
      queryClient.invalidateQueries({ queryKey: [SHIPMENTS_QUERY_KEY] });
      // Remove the specific shipment query from cache
      queryClient.removeQueries({ queryKey: [SHIPMENT_QUERY_KEY, id] });
      // Also invalidate any purchase order queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    }
  });

  return {
    shipments,
    isLoading,
    error,
    createShipment: createShipmentMutation.mutateAsync,
    updateShipment: updateShipmentMutation.mutateAsync,
    deleteShipment: deleteShipmentMutation.mutateAsync,
    addPurchaseOrderItemsToShipment: addPurchaseOrderItemsToShipmentMutation.mutateAsync,
    removePurchaseOrderItemsFromShipment: removePurchaseOrderItemsFromShipmentMutation.mutateAsync,
    isCreating: createShipmentMutation.isLoading,
    isUpdating: updateShipmentMutation.isLoading,
    isDeleting: deleteShipmentMutation.isLoading
  };
};

export const useShipment = (id: string) => {
  const { data: shipment, isLoading, error } = useQuery({
    queryKey: [SHIPMENT_QUERY_KEY, id],
    queryFn: async () => {
      const result = await container.shipmentRepository.getShipment(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!id
  });

  return {
    shipment,
    isLoading,
    error
  };
}; 